import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ConditionListEditor from "./ConditionListEditor";
import RemoveIconButton from "./RemoveIconButton";
import { axisValueOptionLabel, axisValueOptions, type LabelNamesByAxis } from "./axisValueLabels";
import {
  literalValue,
  type Combinator,
  type ConditionNode,
  type RuleActions,
  type WizardParameter,
  type WizardRule,
  type WizardValue,
} from "./clearanceRulesConfig";
import ValueField from "./ValueField";
import type { LidarClearanceAxis } from "../schemas";
import t from "../translations";

type Props = {
  rule: WizardRule;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  parameters: WizardParameter[];
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggleExpanded: () => void;
  onChange: (rule: WizardRule) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

function actionValueLabel(
  axisName: string,
  value: WizardValue,
  axes: LidarClearanceAxis[],
  labelNames: LabelNamesByAxis
): string {
  if (value.kind === "param") return value.name;
  if (value.value === null) return "—";
  const axis = axes.find((candidate) => candidate.name_en === axisName);
  return axis ? axisValueOptionLabel(labelNames, axis.id, value.value) : String(value.value);
}

function ruleTitle(rule: WizardRule, axes: LidarClearanceAxis[], labelNames: LabelNamesByAxis): string {
  if (rule.actions.size === 0) return t.RuleTitlePlaceholder;
  return [...rule.actions.entries()]
    .map(([axisName, value]) => `${axisName}: ${actionValueLabel(axisName, value, axes, labelNames)}`)
    .join(", ");
}

export default function RuleCard({
  rule,
  axes,
  labelNames,
  parameters,
  expanded,
  isFirst,
  isLast,
  onToggleExpanded,
  onChange,
  onRemove,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: Props) {
  const setConditions = (children: ConditionNode[]) =>
    onChange({ ...rule, root: { ...rule.root, children } });

  const setCombinator = (combinator: Combinator) =>
    onChange({ ...rule, root: { ...rule.root, combinator } });

  const updateActions = (mutate: (actions: RuleActions) => void) => {
    const actions = new Map(rule.actions);
    mutate(actions);
    onChange({ ...rule, actions });
  };

  const toggleAxis = (axisName: string, checked: boolean) =>
    updateActions((actions) =>
      checked ? actions.set(axisName, literalValue()) : actions.delete(axisName)
    );

  return (
    <Accordion expanded={expanded} onChange={onToggleExpanded} data-testid="rule-card">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          sx={{ width: "100%", justifyContent: "space-between", alignItems: "center", pr: 1 }}
        >
          <Typography variant="body2">{ruleTitle(rule, axes, labelNames)}</Typography>
          <Stack direction="row" onClick={(event) => event.stopPropagation()}>
            {[
              { icon: <ArrowUpwardIcon fontSize="small" />, label: t.MoveRuleUpButton, onClick: onMoveUp, disabled: isFirst },
              { icon: <ArrowDownwardIcon fontSize="small" />, label: t.MoveRuleDownButton, onClick: onMoveDown, disabled: isLast },
              { icon: <ContentCopyIcon fontSize="small" />, label: t.DuplicateRuleButton, onClick: onDuplicate, disabled: false },
            ].map(({ icon, label, onClick, disabled }) => (
              <IconButton key={label} size="small" aria-label={label} disabled={disabled} onClick={onClick}>
                {icon}
              </IconButton>
            ))}
            <RemoveIconButton aria-label={t.RemoveRuleButton} onClick={onRemove} />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="subtitle2" gutterBottom>
          {t.ConditionsLabel}
        </Typography>
        <ConditionListEditor
          nodes={rule.root.children}
          combinator={rule.root.combinator}
          parameters={parameters}
          onChangeNodes={setConditions}
          onChangeCombinator={setCombinator}
          requireAtLeastOne
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
          {t.ActionsLabel}
        </Typography>
        <Stack spacing={0.5}>
          {axes.map((axis) => {
            const checked = rule.actions.has(axis.name_en);
            const value = rule.actions.get(axis.name_en) ?? literalValue();
            return (
              <Stack
                key={axis.id}
                direction="row"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(event) =>
                        toggleAxis(axis.name_en, event.target.checked)
                      }
                    />
                  }
                  label={axis.name_en}
                  sx={{ width: 220 }}
                />
                {checked && (
                  <ValueField
                    label={t.SeverityValueLabel}
                    value={value}
                    parameters={parameters}
                    onChange={(updated) =>
                      updateActions((actions) => actions.set(axis.name_en, updated))
                    }
                    literalOptions={axisValueOptions(labelNames, axis)}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
